import {
  CardCategorySchema,
  CreateAlertCardResponseSchema,
  FlightAlertsResponseSchema,
  FlightAlertTravelerResponseSchema,
  FlightAlertTripResponseSchema,
  HttpError,
  type CardCategory,
  type CreateAlertCardRequest,
  type CreateAlertCardResponse,
  type FlightAlertsResponse,
  type FlightAlertTravelerResponse,
  type TripView,
} from '@voll-intelligence/domain';
import { z } from 'zod';
import type { HttpService } from '@web/data/services';

export interface FlightAlertsFilters {
  severity: 'all' | 'act' | 'watch';
  vip: boolean;
  q: string;
}

/**
 * Thrown by {@link FlightAlertsRepository.list} when kanban-api answers
 * `503 { error: 'feature_disabled' }` — the org has the flight-alerts panel
 * turned off. A transient `cirium_unavailable` 503 is NOT mapped here: it
 * surfaces as a plain `HttpError` so callers can retry.
 */
export class FlightAlertsDisabledError extends Error {
  constructor(options?: ErrorOptions) {
    super('flight alerts disabled in this environment', options);
    // Subclassing Error keeps `name` as 'Error' unless it is set here, and
    // `name` is what logs and telemetry show.
    this.name = 'FlightAlertsDisabledError';
  }
}

export interface FlightAlertsRepository {
  list: (orgId: string, filters: FlightAlertsFilters) => Promise<FlightAlertsResponse>;
  createCard: (
    orgId: string,
    alertKey: string,
    body: CreateAlertCardRequest,
  ) => Promise<CreateAlertCardResponse>;
  listCardCategories: (orgId: string) => Promise<CardCategory[]>;
  /** The traveler's trip holding the alerted voucher; `null` when no organized trip carries it. */
  trip: (orgId: string, alertKey: string) => Promise<TripView | null>;
  /** The alerted traveler's operational profile + company (customer-api dossier, sans trips). */
  traveler: (orgId: string, alertKey: string) => Promise<FlightAlertTravelerResponse>;
}

const CategoriesSchema = z.object({ categories: z.array(CardCategorySchema) });

function isFeatureDisabled(err: unknown): boolean {
  return (
    err instanceof HttpError &&
    err.status === 503 &&
    (err.details as { error?: string } | undefined)?.error === 'feature_disabled'
  );
}

export function createFlightAlertsRepository(http: HttpService): FlightAlertsRepository {
  return {
    async list(orgId, filters) {
      try {
        return await http.get(`/orgs/${orgId}/alerts/flights`, {
          query: { severity: filters.severity, vip: filters.vip, q: filters.q },
          schema: FlightAlertsResponseSchema,
        });
      } catch (err) {
        if (isFeatureDisabled(err)) throw new FlightAlertsDisabledError({ cause: err });
        throw err;
      }
    },
    createCard: (orgId, alertKey, body) =>
      http.post(`/orgs/${orgId}/alerts/flights/${encodeURIComponent(alertKey)}/card`, body, {
        schema: CreateAlertCardResponseSchema,
      }),
    listCardCategories: async (orgId) =>
      (await http.get(`/orgs/${orgId}/card-categories`, { schema: CategoriesSchema })).categories,
    trip: async (orgId, alertKey) =>
      (
        await http.get(`/orgs/${orgId}/alerts/flights/${encodeURIComponent(alertKey)}/trip`, {
          schema: FlightAlertTripResponseSchema,
        })
      ).trip,
    traveler: (orgId, alertKey) =>
      http.get(`/orgs/${orgId}/alerts/flights/${encodeURIComponent(alertKey)}/traveler`, {
        schema: FlightAlertTravelerResponseSchema,
      }),
  };
}
